import { useRef, useState, useEffect } from "react";
import debug from "src/modules/debug-logger";
import decodeOgg from "src/browser/decodeOgg";

export const Status = {
  RESTING: "RESTING",
  PLAYING: "PLAYING",
  LOADING: "LOADING"
};

const ErrorMessages = {
  PLAY_CANCELED: "PLAY_CANCELED"
};

export default function useAudioController({ srcResolver, onEnded }) {
  const [hasPlayError, setHasPlayError] = useState(false);
  const [status, setStatus] = useState(Status.RESTING);
  const [audioIdentifier, setAudioIdentifier] = useState(null);

  const srcResolverRejectRef = useRef();
  const audioRef = useRef(new Audio());

  useEffect(
    () => () => {
      audioRef.current.pause();
      audioRef.current.onerror = null;
    },
    []
  );

  async function play({
    identifier,
    loop = false,
    duration,
    playDuration = duration,
    srcResolverInput,
    src
  } = {}) {
    pause();
    setAudioIdentifier(identifier);
    try {
      if (src) {
        audioRef.current.src = src;
      } else {
        setStatus(Status.LOADING);
        const srcResolverPromise = srcResolver(identifier, srcResolverInput);
        const src = await srcResolverPromise;
        audioRef.current.src = await new Promise((resolve, reject) => {
          try {
            srcResolverRejectRef.current = reject;
            srcResolverRejectRef.current = null;
            resolve(src.url || src);
          } catch (error) {
            reject(error.message);
          }
        });
      }
      audioRef.current.loop = loop;
      audioRef.current.onerror = () => tryPlayDecoded();
      audioRef.current.play();
      setStatus(Status.PLAYING);
    } catch (error) {
      setStatus(Status.RESTING);
      if (error.message === ErrorMessages.PLAY_CANCELED) {
        return;
      }
      debug.lullaby("srcResolver failed", error);
      setHasPlayError(true);
      return error;
    }
  }

  async function tryPlayDecoded() {
    setStatus(Status.LOADING);
    const response = await fetch(audioRef.current.src);
    const arrayBuffer = await response.arrayBuffer();

    decodeOgg(audioRef.current, arrayBuffer, () => setStatus(Status.PLAYING));
  }

  function clearPlayError() {
    setHasPlayError(false);
  }

  function getStatusForIdentifier(identifier) {
    return identifier === audioIdentifier ? status : Status.RESTING;
  }

  function pause() {
    audioRef.current.pause();
    if (status === Status.LOADING && srcResolverRejectRef.current) {
      srcResolverRejectRef.current(new Error(ErrorMessages.PLAY_CANCELED));
      srcResolverRejectRef.current = null;
    }
    setStatus(Status.RESTING);
    setAudioIdentifier(null);
  }

  function seek(time) {
    audioRef.current.pause();
    audioRef.current.currentTime = time / 1000;
  }

  function seekCommitted(time) {
    audioRef.current.currentTime = time / 1000;
    audioRef.current.play();
  }

  function getCurrentTime() {
    return audioRef.current.currentTime * 1000;
  }

  function getDuration() {
    return audioRef.current.duration * 1000;
  }

  audioRef.current.addEventListener("ended", () => {
    setStatus(Status.RESTING);
    setAudioIdentifier(null);
    onEnded && onEnded();
  });
  audioRef.current.addEventListener("timeupdate", () => {
    // workaroud to make loop continuous without sound gap
    if (audioRef.current.loop) {
      if (audioRef.current.currentTime + 0.44 > audioRef.current.duration) {
        audioRef.current.currentTime = 0;
      }
    }
  });

  return {
    play,
    status,
    getStatusForIdentifier,
    pause,
    seek,
    seekCommitted,
    getCurrentTime,
    getDuration,
    hasPlayError,
    clearPlayError,
    Status
  };
}
